/**
 * login.component.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Subscription } from "rxjs/Rx";

import { AlertService } from "../common/alert.service";
import { SLOW_ATTACH, SLOW_REMOVAL } from "../support/utilities";

import { AuthService } from "./auth.service";
import { PasswordCredentials } from "./identity/credentials";
import { LoginService } from "./login.service";

/**
 * The {@link LoginComponent} is the primary page for user authentication.
 */
@Component({
  selector: "login",
  template: `
  <div class="container">
  <div id="loginContainer" class="well center-block">
  <form (ngSubmit)="attemptLogIn($event)" [formGroup]="form">
    <div *ngIf="presentAccessWarning" class="text-center text-danger">
      <em>You must be logged in to access that page!</em>
    </div>
    <div *ngIf="failedPrevious" class="text-center text-warning">
      Your username and/or password are incorrect. Please try again.
    </div>
    <div *ngIf="!login.ready" class="text-center text-info" @slowAttach>
      You are either not connected to the server, or the server is already
      processing your login attempt. If this message does not go away shortly,
      please reload and try again...
    </div>
    <div class="form-group">
      <label>Username</label>
      <input
		type="text"
		autocomplete="username"
        formControlName="username"
        autofocus
        class="form-control"
        placeholder="Username...">
    </div>
    <div class="form-group">
      <label>Password</label>
      <input
		type="password"
		autocomplete="current-password"
        formControlName="password"
        class="form-control"
        placeholder="Password...">
    </div>
    <div class="form-group">
      <input
        type="submit"
        class="btn btn-primary form-control"
        value="Login"
        [disabled]="!login.ready">
    </div>
    <div class="text-center">
      <a class="text-warning" routerLink="/forgot-password">
        Reset / change your password
      </a>
    </div>
  </form>
  </div>
  </div>
  `,
  styles: [
    "#loginContainer { width: 400px; max-width: 90%;}",
    "a {text-decoration: underline;}"
  ],
  animations: [ SLOW_ATTACH, SLOW_REMOVAL ]
})
export class LoginComponent implements OnInit, OnDestroy
{

  /**
   * Flag indicating whether the last attempt failed, triggering a text blurb.
   */
  public failedPrevious : boolean;

  /**
   * Flag indicating that a route access was denied because it requres login.
   */
  public presentAccessWarning : boolean = false;

  public form : FormGroup;

  /**
   * Construct a new {@link LoginComponent}.
   */
  public constructor (
    private auth : AuthService,
    protected alerts : AlertService,
    protected login : LoginService
  )
  {
    // Nothing
  }

  /**
   * On initialization, create the form controls, detect whether a post-auth
   * redirect is pending, and subscribe to messaging readiness in order to
   * present notifications about disconnections.
   */
  public ngOnInit () : void
  {
    this.constructForm();
    this.presentAccessWarning = (!!this.auth.redirectTo);
  }

  /**
   * Unsubscribe the ready flag when the component is destroyed.
   */
  public ngOnDestroy () : void
  {
    // nothin'
  }

  private constructForm () : void
  {
    this.form = new FormGroup(
      {
        username: new FormControl(null, Validators.required),
        password: new FormControl(null, Validators.required)
      }
    );
  }

  /**
   * Use the {@link AuthService} to attempt a login, and redirect if the attempt
   * was successful.
   *
   * @return {boolean} Always false, to prevent HTML form submit navigation.
   */
  public attemptLogIn () : boolean
  {
    const credentials : PasswordCredentials = new PasswordCredentials(
      this.auth.hasher, this.auth.salter);
    credentials.username = this.form.get("username").value;
    credentials.password = this.form.get("password").value;

    try
    {
      // TODO: presubmit validation?
      this.login.attempt(credentials).subscribe(
        (authenticated : boolean) => this.failedPrevious = !authenticated,
        (error) => console.error(error),
        () => this.auth.redirectIfAuthed()
      );
    }
    catch (e)
    {
      // ok hopefully this doesn't happen anymore
      this.alerts.error(
        "Could not log in -- connection has closed. (You may need to reload.)");
      console.error(e);
    }

    // Since this is hooked into the form's onSubmit, returning false prevents
    // the page from reloading
    return false;
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/auth/login.component.ts